import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core";
import { ToastrService } from "../toastr.service";
import { TranslateService } from "@ngx-translate/core";
import { imageDimensionsFromData } from "image-dimensions";
import { Subscription } from "rxjs";
import { finalize } from "rxjs/operators";
import {
  ALLOWED_UPLOAD_IMAGE_MIME_TYPES,
  Image,
  ImageUploadFn,
} from "./image.interface";
import { mediaFilePreview } from "../utils/media-file-preview.util";

@Component({
  selector: "app-image-uploader",
  templateUrl: "./image-uploader.component.html",
  styleUrls: ["./image-uploader.component.scss"],
  standalone: false,
})
export class ImageUploaderComponent implements OnInit, OnDestroy {
  @ViewChild("fileInput") private fileInputRef?: ElementRef<HTMLInputElement>;

  @Input() previewUrl: string | null = null;
  @Input() existingImageUrl: string;
  @Input() allowedFileExtensions: readonly string[] = [
    ...ALLOWED_UPLOAD_IMAGE_MIME_TYPES,
  ];
  @Input() wrongTypeMessage: string = this.translate.instant(
    "company.wrongType",
  ) as string;
  @Input() description: string = this.translate.instant(
    "company.photoClick",
  ) as string;
  @Input() additionalDescription: string;
  @Input() previewImageStyles: string;
  @Input() uploadFn: ImageUploadFn | null = null;
  @Output() private onFileChange = new EventEmitter<Image>();
  @Output() private onFileRemove = new EventEmitter<void>();

  busy = false;
  private uploadSub?: Subscription;

  constructor(
    private translate: TranslateService,
    private toastr: ToastrService,
    private cdr: ChangeDetectorRef,
  ) {}

  get canRemove(): boolean {
    return this.onFileRemove.observed;
  }

  get filePreview() {
    return mediaFilePreview(this.previewUrl);
  }

  ngOnInit(): void {
    if (this.existingImageUrl && !this.previewUrl) {
      this.previewUrl = this.existingImageUrl;
    }
  }

  ngOnDestroy(): void {
    this.uploadSub?.unsubscribe();
  }

  uploadFile = (files: FileList | null) => {
    if (this.busy || !files?.length) return;
    const file = files[0];
    const mimeType = file.type;

    if (!this.allowedFileExtensions.includes(mimeType)) {
      this.resetFileInput();
      return this.toastr.error(this.wrongTypeMessage);
    }

    if (file.size > 8 * 1024 * 1024) {
      this.resetFileInput();
      return this.toastr.error(
        this.translate.instant("company.fileTooLarge") as string,
      );
    }

    const previousPreview = this.previewUrl;
    this.busy = true;
    this.cdr.markForCheck();

    const reader = new FileReader();
    reader.readAsDataURL(file);

    reader.onload = async () => {
      const localPreview = reader.result as string;
      this.previewUrl = localPreview;

      let width: number | undefined;
      let height: number | undefined;
      try {
        const dimensions = imageDimensionsFromData(
          new Uint8Array(await file.arrayBuffer()),
        );
        width = dimensions?.width;
        height = dimensions?.height;
      } catch {
        // keep undefined dimensions
      }

      const base: Image = { file, width, height, previewUrl: localPreview };

      if (!this.uploadFn) {
        this.onFileChange.emit(base);
        this.busy = false;
        this.resetFileInput();
        this.cdr.markForCheck();
        return;
      }

      this.uploadSub?.unsubscribe();
      this.uploadSub = this.uploadFn(file)
        .pipe(
          finalize(() => {
            this.busy = false;
            this.resetFileInput();
            this.cdr.markForCheck();
          }),
        )
        .subscribe({
          next: (result) => {
            const previewUrl =
              typeof result === "string" ? result : result.previewUrl;
            const uploadData =
              typeof result === "string" ? undefined : result.uploadData;
            const finalPreview = previewUrl.includes("?")
              ? previewUrl
              : `${previewUrl}?t=${Date.now()}`;
            this.previewUrl = finalPreview;
            this.onFileChange.emit({
              ...base,
              previewUrl: finalPreview,
              uploadData,
            });
          },
          error: () => {
            this.previewUrl = previousPreview;
          },
        });
    };

    reader.onerror = () => {
      this.previewUrl = previousPreview;
      this.busy = false;
      this.resetFileInput();
      this.cdr.markForCheck();
    };
  };

  onDragOver = (event: DragEvent) => event.preventDefault();

  onDropSuccess = (event: DragEvent) => {
    event.preventDefault();
    if (this.busy) return;
    this.uploadFile(event.dataTransfer?.files ?? null);
  };

  removeImage($event: MouseEvent) {
    $event.stopPropagation();
    if (this.busy || !this.canRemove) return;
    this.previewUrl = null;
    this.resetFileInput();
    this.onFileRemove.emit();
  }

  private resetFileInput(): void {
    const input = this.fileInputRef?.nativeElement;
    if (input) {
      input.value = "";
    }
  }
}
